export const en = {
  app: { title: "Melee Royale" },
  lobby: {
    tagline:
      "No weapons. A steel cage that closes every 27 seconds. Every arm and leg is yours to throw, and every hit lands as hard as your body actually made it.",
    nickname: "Your name in the cage",
    nicknamePlaceholder: "Fighter",
    nicknameHint: "1 to 16 characters. Others see it on your nameplate.",
    modesLabel: "How do you want to fight",
    modes: { practice: "Practice", create: "Create", join: "Join" },
    fewer: "Fewer",
    more: "More",
    practice: {
      intro: "Just you against bots. The fight starts as soon as you step in.",
      bots: "Bots",
      botsHint: "From 1 to 4.",
      submit: "Start practice",
    },
    create: {
      intro:
        "Open a session other players can join from their menu. You start the fight once enough players are in.",
      players: "Players needed",
      playersHint: "Humans, you included. At least 2; more can join until you start.",
      bots: "Bots",
      botsHint: "Up to 5 bots join when the fight starts.",
      pin: "PIN (optional)",
      pinPlaceholder: "4 digits",
      pinHint: "Players will need these 4 digits to join. Leave it empty for an open session.",
      pinIncomplete: "A PIN has exactly 4 digits.",
      submit: "Create session",
    },
    join: {
      title: "Open sessions",
      refresh: "Refresh",
      empty: "No open sessions right now",
      emptyHint: "Create one for your friends, or practice while you wait.",
      error: "Could not load the sessions.",
      host: "{name}'s session",
      players: "{humans}/{min} players",
      bots: { one: "{n} bot", other: "{n} bots" },
      noBots: "no bots",
      ready: "Ready",
      full: "Full",
      locked: "PIN",
      pin: "Session PIN",
      submit: "Join",
    },
    requesting: "Opening a cage…",
    connecting: "Stepping in…",
    errors: {
      generic: "Something went wrong. Try again.",
      max_fights: "All cages are busy. Try again in a minute.",
      room_unavailable: "Could not open a cage. Try again.",
      not_found: "That session is gone.",
      started: "That session already started.",
      full: "That session is full.",
      bad_pin: "Wrong PIN.",
      bad_request: "Check the form and try again.",
      expired: "The session closed before it started.",
    },
    rules: {
      cage: {
        title: "The cage closes",
        body: "Every 27 s the walls move in. Pinned against a wall, you get crushed.",
      },
      limbs: {
        title: "Four limbs, ten parts",
        body: "Strike with either arm or leg. Health is tracked per body part.",
      },
      clock: {
        title: "Three minutes",
        body: "Last one standing wins. On the bell, the healthiest fighter takes it.",
      },
    },
  },
  waiting: {
    title: "Waiting room",
    session: "{name}'s session",
    needs: "Needs {min} players",
    bots: { one: "{n} bot joins at the start", other: "{n} bots join at the start" },
    noBots: "No bots",
    pin: "PIN {pin}",
    locked: "PIN protected",
    players: "Players",
    host: "host",
    you: "you",
    moreNeeded: { one: "Waiting for {n} more player", other: "Waiting for {n} more players" },
    readyHost: "Enough players are in. Start when you are ready; more can still join.",
    readyGuest: "Waiting for {host} to start the fight.",
    closesIn: "Closes in {time} if it does not start",
    start: "Start fight",
    leave: "Leave",
    closed: "This session closed.",
    lost: "Lost the connection to the session.",
    back: "Back to menu",
    notReady: "Not enough players yet.",
  },
  match: {
    clickToPlay: "Click to take control",
    paused: "Paused",
    pausedHint: "Esc releases the mouse. Click to fight on.",
    resume: "Back to the fight",
    controlsTitle: "Controls",
    controls: {
      move: "Move",
      look: "Look: pick who you hit and turn your guard to them",
      step: "Quick step (with a direction)",
      duck: "Crouch",
      lean: "Slip left / right",
      armL: "Left arm (punches go for the head)",
      armR: "Right arm",
      legL: "Left leg (kicks go for the body)",
      legR: "Right leg",
      aimAlt: "Hold with a strike: a punch to the body, a kick to the head",
      aimLow: "Hold with a strike: a kick to the legs (a punch goes no lower than the body)",
      arcs: "Hold with a strike: hook / uppercut",
      combo: "Tap quickly: up to 3 strikes chain into a combo",
      guard: "Cover your head; let go and the hands relax",
      guardBody: "Cover your body",
      check: "Cover your legs: the lead knee comes up and checks the kick",
      stance: "Switch stance (lead foot and hand)",
    },
    settingsTitle: "Look and sound",
    settings: {
      invertY: "Invert vertical look",
      invertYHint: "Mouse down looks up.",
      volume: "Sound volume",
      volumeHint: "A whoosh when a limb is thrown, a hit when it lands.",
    },
    mouse: { left: "Left click", right: "Right click", move: "Mouse" },
    waiting: "Waiting for fighters",
    countdown: "Fight in",
    fight: "Fight",
    clock: "Clock",
    nextShrink: "Walls move in",
    warning: "Walls closing",
    alive: "Alive",
    stamina: "Stamina",
    exhausted: "Exhausted",
    stance: { orthodox: "Orthodox · left leads", southpaw: "Southpaw · right leads" },
    checking: "Checking",
    /** What the guard covers, by zone: head, body, legs. */
    covering: ["Covering head", "Covering body", "Covering legs"],
    spectating: "You are out. Watching.",
    connectionLost: "Connection lost.",
    buildMismatch: "This page is an old build. Reload to get back in.",
    parts: [
      "head",
      "torso",
      "upper arm",
      "upper arm",
      "forearm",
      "forearm",
      "thigh",
      "thigh",
      "shin",
      "shin",
    ],
    /** Names the hit readout derives from limb, arc and lead/rear: [arc][lead, rear]. */
    strikes: {
      arm: [
        ["jab", "cross"],
        ["lead hook", "rear hook"],
        ["lead uppercut", "rear uppercut"],
      ],
      leg: [
        ["lead push kick", "rear push kick"],
        ["lead roundhouse", "rear roundhouse"],
        ["lead rising kick", "rear rising kick"],
      ],
    },
    hit: {
      line: "{attacker} → {target}: {strike} · {part} · {damage}",
      blocked: "blocked ({absorb}%)",
      checked: "checked, their shin −{recoil}",
      parried: "parried",
      counter: "counter",
      steppedIn: "stepped in",
      jammed: "jammed",
      overreach: "over-reached",
      collapsed: "guard collapsed",
    },
    /** Captions under the damage numbers. */
    pop: {
      blocked: "blocked",
      parried: "parried",
      checked: "checked",
      guardBroke: "guard broke",
      counter: "counter",
      jammed: "jammed",
      recoil: "shin",
      cage: "cage",
    },
    tally: { dealt: "Dealt", taken: "Taken" },
    events: {
      shrink: "The cage closed",
      crushed: "{name} is being crushed",
      eliminated: "{name} is out",
      eliminatedBy: "{name} was knocked out by {by}",
      matchEnd: "Match over",
    },
    leave: "Leave",
  },
  results: {
    title: "Match over",
    winner: "{name} wins",
    youWin: "You win",
    reasonLastStanding: "Last one standing",
    reasonTimeout: "On the bell, healthiest fighter",
    noWinner: "Nobody survived the cage",
    you: "you",
    health: "health",
    out: "out",
    damage: "dealt {dealt} · took {taken}",
    playAgain: "Back to the menu",
  },
  common: { loading: "Loading", close: "Close" },
} as const;

export type Dictionary = typeof en;
